import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaSignOutAlt, FaArrowLeft } from 'react-icons/fa';
import ThemeSwitcher from './ThemeSwitcher';
import Logo from './Logo';
import useEditorStore from '../store/useEditorStore';
import { logout } from '../utils/auth';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { artboardName, setArtboardName, isSaving } = useEditorStore();
  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSave = () => {
    if (isSaving) return;
    window.dispatchEvent(new CustomEvent('saveArtboard'));
  };

  const handleNameClick = () => {
    if (isEditorPage) {
      setIsEditing(true);
      setEditValue(artboardName);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setEditValue(newName);
  };

  const handleNameSave = () => {
    const newName = editValue.trim() || 'Untitled Artboard';
    setArtboardName(newName);
    setIsEditing(false);
  };

  const handleNameCancel = () => {
    setIsEditing(false);
    setEditValue(artboardName);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      handleNameCancel();
    }
  };

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const isEditorPage = location.pathname.startsWith('/editor');

  if (isEditorPage) {
    return (
      <header className="relative z-[1000] flex items-center justify-between border-b border-gray-200/60 bg-white/80 px-3 py-3 backdrop-blur-sm dark:border-gray-700/60 dark:bg-gray-900/80">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <button
            onClick={() => navigate('/projects')}
            title="Back to Projects"
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
          >
            <FaArrowLeft size={14} />
          </button>
          <div className="flex min-w-0 flex-1 items-center">
            {isEditing ? (
              <input
                ref={inputRef}
                value={editValue}
                onChange={handleNameChange}
                onBlur={handleNameSave}
                onKeyDown={handleNameKeyDown}
                placeholder="Enter artboard name"
                className="max-w-[220px] rounded-md border border-blue-500 bg-white px-2 py-1 text-sm font-medium text-gray-900 outline-none ring-2 ring-blue-500/20 dark:bg-gray-800 dark:text-white"
              />
            ) : (
              <div
                onClick={handleNameClick}
                title="Click to edit"
                className="max-w-[260px] cursor-pointer truncate rounded-md px-2 py-1 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-100 dark:text-white dark:hover:bg-gray-800"
              >
                {artboardName}
              </div>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={isSaving}
            title={isSaving ? "Saving..." : "Save artboard (Ctrl+S)"}
            className="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm transition-all hover:shadow-md hover:brightness-110 active:scale-[0.97] disabled:pointer-events-none disabled:opacity-60"
          >
            {isSaving ? (
              <>
                <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                <span className="hidden sm:block">Saving...</span>
              </>
            ) : (
              <>
                <span className="material-symbols-rounded text-[18px]" style={{ fontVariationSettings: '"FILL" 1' }}>save</span>
                <span className="hidden sm:block">Save</span>
              </>
            )}
          </button>
        </div>
        <div className="ml-3 flex flex-shrink-0 items-center gap-0.5">
          <ThemeSwitcher />
          <button
            onClick={() => navigate('/settings')}
            title="Account Settings"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
          >
            <span className="material-symbols-rounded text-[20px]">settings</span>
          </button>
          <div className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />
          <button
            onClick={handleLogout}
            title="Logout"
            className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
          >
            <FaSignOutAlt size={14} />
            <span className="hidden md:block">Logout</span>
          </button>
        </div>
      </header>
    );
  }

  return (
    <header className="relative z-[1000] flex items-center justify-between border-b border-gray-200/60 bg-white/80 px-4 py-3 backdrop-blur-sm dark:border-gray-700/60 dark:bg-gray-900/80">
      <Logo onClick={() => navigate('/projects')} className="relative z-[1]" />
      <nav className="relative z-[1] flex items-center gap-1">
        <ThemeSwitcher />
        <button
          onClick={() => navigate('/settings')}
          title="Account Settings"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
        >
          <span className="material-symbols-rounded text-[20px]">settings</span>
        </button>
        <div className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />
        <button
          onClick={handleLogout}
          className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
        >
          <FaSignOutAlt size={14} />
          Logout
        </button>
      </nav>
    </header>
  );
};

export default Header;
